import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Inject,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  Renderer2,
  ViewChild,
  ViewContainerRef,
  ComponentRef,
  ChangeDetectionStrategy,
  DestroyRef
} from '@angular/core';
import {
  PlatformInformationService,
  PlatformInformationInterface,
  PlatformurlPipe,
  PlatformassetPipe
} from '../../../../../projects/platform-manager/src/public_api';
import { CurrencyService } from '../../../core/services/currency.service';
import { Shop, unbookableType } from '../../entities/shop';
import { TranslateService } from '@ngx-translate/core';
import { BookingParametersInterface } from '../../entities/bookingParameters';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Router, UrlTree } from '@angular/router';
import { PartnerLinkService } from '../../../core/services/partner-link.service';
import { SsrCookieService as CookieService } from 'ngx-cookie-service-ssr';
import { ShopDetailService } from '../../services/shop-detail.service';
import { BROWSER_WINDOW } from '../../../core/injection-tokens/browser-window';
import { ResolutionService } from '../../../core/services/resolution.service';
import { MinDelayCountdownService } from '../../services/min-delay-countdown.service';
import { FOLD_OUT_TOGGLE_ANIMATION } from '../../../core/animations/FOLD_OUT_TOGGLE_ANIMATION';
import { SwipeDetectionServiceService, SwipeDirection } from '../../services/swipe-detection-service.service';
import { AbTestService } from '@ab-test';
import {
  CommonModule,
  DatePipe,
  DOCUMENT,
  IMAGE_CONFIG,
  IMAGE_LOADER,
  ImageLoaderConfig,
  NgOptimizedImage
} from '@angular/common';
import { DiscountTickerService } from '../../../core/services/discount-ticker.service';
import { DiscounttickerComponent } from '../../../shared/saleslocation/shop/discountticker/discountticker.component';
import { NotificationService, Notification } from '../../services/notification.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlpitranslatePipe } from '../../../core/pipes/alpitranslate.pipe';
import {
  IconComponent,
  box,
  changeEquipment,
  damage,
  delivery,
  depot,
  discount,
  discountArrow,
  freeChildAdult,
  infoCircle,
  place,
  provideIcons,
  reserve,
  smChild,
  snowBullet2,
  topShop
} from '../../icons';
import { ProceedToProductListComponent } from '@shared/components';
import { UnbookableInfoComponent } from './unbookable-info/unbookable-info.component';
import { alpiTooltipDirective } from '../../components/alpi-tooltip/alpiTooltip';
import { VsLocationDropOffAdvantagesComponent } from './vs-location-drop-off-advantages/vs-location-drop-off-advantages.component';
import { DistanceDisplayComponent } from '../../components/distance-display/distance-display.component';
import { SelectedHotelService } from '../../services/selected-hotel.service';
import { CrowdinTranslationService } from '../../services/crowdin-translation.service';
import { loaderMap } from './translations/loader-map';

@Component({
  selector: 'app-shop',
  templateUrl: './shop.component.html',
  styleUrls: ['./shop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [FOLD_OUT_TOGGLE_ANIMATION],
  imports: [
    CommonModule,
    AlpitranslatePipe,
    IconComponent,
    UnbookableInfoComponent,
    ProceedToProductListComponent,
    alpiTooltipDirective,
    NgOptimizedImage,
    // lazy imports
    VsLocationDropOffAdvantagesComponent,
    DistanceDisplayComponent,
    PlatformurlPipe,
    PlatformassetPipe
  ],
  providers: [
    /*
    TODO:
    1: zone on-a-per-satellite-basis
    2: use https://www.npmjs.com/package/sharp on node/express with custom provider https://v19.angular.dev/guide/image-optimization#custom-loaders
    {
      provide: 'IMAGE_LOADER',
      useFactory: () => {
        const domainService = inject(DomainService);
        const domain = domainService.getImageDomain(); // works in SSR & browser
        return provideCloudflareLoader(`${domain}/cdn-cgi/image/`);
      },
    },
    */
    // provideCloudflareLoader('https://core.alpy.com/'),
    {
      provide: IMAGE_CONFIG,
      useValue: {
        breakpoints: [16, 48, 128, 384, 640, 1200]
      }
    },
    {
      // RS TODO: this image loader will most likely be used across multiple components,
      // we should extract it
      provide: IMAGE_LOADER,
      useFactory: (platformService: PlatformInformationService) => {
        // only alpy.com/fun is on cloudflare so we only have the cdn-cgi there
        if (platformService.getCurrentPlatformShortcode() === 'ar') {
          return (config: ImageLoaderConfig) => {
            const src = config.src.replace(/^\/+/, '');
            const ops: { [key: string]: string | number } = {
              quality: config.loaderParams?.quality ?? 'low',
              format: config.loaderParams?.format ?? 'auto'
            };
            if (config.width) {
              ops.width = config.width;
            }
            const vals = { ...config.loaderParams, ...ops };

            return `${platformService.getHostname().replace(/\/+$/, '')}/cdn-cgi/image/${Object.keys(vals)
              .map(key => `${key}=${vals[key]}`)
              .join(',')}/${src}`;
          };
        }
        // all other satellites
        return (config: ImageLoaderConfig) => {
          return `${platformService.getHostname()}${config.src}`;
          // in case we need to implement something with sharp support on our pipeline:
          // let imageCdnPath = `${platformService.getOrigin().replace(/\/+$/, '')}/components/cdn-image?origin=${platformService.getHostname()}${config.src}`;
          // if (config.loaderParams?.quality) {
          //   imageCdnPath += `&quality=${config.loaderParams?.quality}`;
          // }
          // if (config.width) {
          //   imageCdnPath += `&width=${config.width}`;
          // }
          // if (config.loaderParams?.format) {
          //   imageCdnPath += `&format=${config.loaderParams?.format}`;
          // }
          // return imageCdnPath;
        };
      },
      deps: [PlatformInformationService]
    },
    provideIcons({
      place,
      discountArrow,
      topShop,
      discount,
      delivery,
      smChild,
      damage,
      depot,
      changeEquipment,
      snowBullet2,
      freeChildAdult,
      reserve,
      infoCircle,
      box
    })
  ],
  host: { ngSkipHydration: 'true' }
})
export class ShopComponent implements OnInit, OnChanges {
  @ViewChild('discountTickerAnchor', { read: ViewContainerRef, static: false })
  protected discountTickerAnchor: ViewContainerRef;
  private tickerRef: ComponentRef<unknown>;

  shopImages: { bigWebP: string; smallWebP: string; big: string; small: string }[] = [];
  shopImageIndex = 0;

  @Input() shop: Shop;
  @Input() userDefinedSearch = false;
  @Input() isLoadingNewPriceData = false;
  @Input() isLoadingNewOutOfTownPriceData = false;
  @Input() bookingParameters: BookingParametersInterface;
  @Input() showAdditionalElements: boolean;
  @Input() showDistanceBadge = true;
  @Input() isPartner = false;
  @Input() showAlpinFlexiBadge = false;
  @Input() freeFlexiUntil = null;
  @Input() sortBy: string;

  currencyCode = this.currencyService.getActualCurrency();
  openedMobileOffers = false;
  priceinfo = false;
  isBrowser = this.platformInformationService.getIsBrowser();
  currentLang = this.translateService.currentLang;
  translatedProperties: Array<string> = [];
  showDetails = false;
  showDetailsEmitter = new EventEmitter<boolean>();
  countdownUntilBookingStop: number;
  currentPlatformShortcode = this.platformInformationService.getCurrentPlatformShortcode();
  isSmallerThanLargeDevice = this.resolutionService.isSmallerThanLargeDevice();
  shopLink: string | UrlTree;
  adults: Array<number>;
  children: Array<number>;
  productLink: string;
  cancellationInsuranceName = '';
  showGroupBookingTestCondition = false;
  navToPSTest = this.abTestService ? this.abTestService.getVersion('shopNavToPS') : 'v1';

  constructor(
    private platformInformationService: PlatformInformationService,
    private partnerLinkService: PartnerLinkService,
    private currencyService: CurrencyService,
    private translateService: TranslateService,
    private localizedRouterService: LocalizeRouterService,
    private router: Router,
    public cookieService: CookieService,
    private shopDetailService: ShopDetailService,
    @Inject(BROWSER_WINDOW) private window,
    public resolutionService: ResolutionService,
    private changeDetector: ChangeDetectorRef,
    private minDelayCountdownService: MinDelayCountdownService,
    private swipeDetectionService: SwipeDetectionServiceService,
    private renderer2: Renderer2,
    private abTestService: AbTestService,
    @Inject(DOCUMENT) private document: Document,
    protected discountTickerService: DiscountTickerService,
    protected notificationService: NotificationService,
    protected selectedHotelService: SelectedHotelService,
    protected datePipe: DatePipe,
    protected destroyRef: DestroyRef,
    private crowdinService: CrowdinTranslationService
  ) {}

  private addLCP() {
    if (this.shop.position === 0 && !this.isBrowser) {
      // mobile webp preconnect
      const linkMobileWebP = this.renderer2.createElement('link');
      linkMobileWebP.rel = 'preload';
      linkMobileWebP.fetchpriority = 'high';
      this.renderer2.setAttribute(linkMobileWebP, 'as', 'image');
      this.renderer2.setAttribute(linkMobileWebP, 'media', '(max-width: 419px)');
      linkMobileWebP.href = this.shop.imagePathBigWebp;
      linkMobileWebP.type = 'image/webp';
      this.renderer2.appendChild(this.document.head, linkMobileWebP);

      // desktop webp preconnect
      const link = this.renderer2.createElement('link');
      link.rel = 'preload';
      link.fetchpriority = 'high';
      this.renderer2.setAttribute(link, 'as', 'image');
      this.renderer2.setAttribute(link, 'media', '(min-width: 420px)');
      link.href = this.shop.imagePathBigWebp;
      link.type = 'image/webp';
      this.renderer2.appendChild(this.document.head, link);
    }
  }

  touchStartMobile(event: TouchEvent) {
    this.swipeDetectionService.start(event, true);
  }

  touchEndMobile(event: TouchEvent) {
    const direction = this.swipeDetectionService.end(event);
    if (direction === SwipeDirection.Right) {
      this.shopImageIndex--;
      if (this.shopImageIndex < 0) {
        this.shopImageIndex = this.shopImages.length - 1;
      }
      this.changeDetector.detectChanges();
    } else if (direction === SwipeDirection.Left) {
      this.shopImageIndex++;
      if (this.shopImageIndex >= this.shopImages.length) {
        this.shopImageIndex = 0;
      }
      this.changeDetector.detectChanges();
    }
  }

  private updateAdultsAndKids() {
    if (this.bookingParameters) {
      this.adults = this.bookingParameters.ages.filter(age => age >= 18);
      this.children = this.bookingParameters.ages.filter(age => age <= 17);
    }
  }

  private prepareProductLink() {
    if (this.bookingParameters) {
      const route = this.localizedRouterService.translateRoute('/SKIHIREENTRY/SKIHIRE.PRODUCTS');
      const routeWithPartner = this.partnerLinkService
        .getUrlReplacingPartnerAfterPrefix(route, this.localizedRouterService.translateRoute('/SKIHIREENTRY'))
        .replace('partner-/', 'partner');
      const queryParams = !this.bookingParameters.userDefinedSearch
        ? { shopId: this.shop.id, showDatepicker: '1' }
        : { shopId: this.shop.id };

      const newLink = this.router
        .createUrlTree([routeWithPartner], {
          queryParams: queryParams
        })
        .toString();
      if (this.productLink != newLink) {
        this.productLink = newLink;
      }
    }
  }

  private prepareShopLink(updateChanges = true) {
    if (this.navToPSTest === 'v1') {
      // nav to shop page
      this.shopLink = this.localizedRouterService.translateRoute(
        this.partnerLinkService.getUrlWithPartner(
          '/SKIHIREENTRY',
          this.shop.townSlugs[this.translateService.currentLang] + '/' + this.shop.shopSlug
        )
      ) as string; //.replace('partner-', 'partner-/');
    } else {
      // nav to product page
      this.shopLink = this.router
        .createUrlTree(
          [
            this.partnerLinkService.getUrlReplacingPartnerAfterPrefix(
              this.localizedRouterService.translateRoute('/SKIHIREENTRY/SKIHIRE.PRODUCTS'),
              this.localizedRouterService.translateRoute('/SKIHIREENTRY')
            )
          ],
          {
            queryParams: {
              shopId: this.shop.id,
              showDatepicker: this.bookingParameters.userDefinedSearch ? null : '1',
              shopInfoExpanded: this.navToPSTest === 'v3' ? '1' : null
            }
          }
        )
        .toString();
      if (updateChanges) {
        this.changeDetector.detectChanges();
      }
    }
  }

  private updateShowGroupBookingTestCondition(cdr = true) {
    this.platformInformationService.customerCountryCode$.subscribe(code => {
      // if customer NOT in AT, CH, LI or IT AND not on a Partner Page AND shop is not 'isSensitive', show group discounts
      if (!['AT', 'CH', 'LI', 'IT'].includes(code) && !this.isPartner && !this.shop.isSensitive) {
        this.showGroupBookingTestCondition = true;
      } else {
        // keep showing as usual, no test condition
        this.showGroupBookingTestCondition = false;
      }
      if (cdr) {
        this.changeDetector.markForCheck();
        this.changeDetector.detectChanges();
      }
    });
  }

  /**
   * preps all images to be used inside the image carousel
   */
  private prepareShopImages() {
    this.shopImages = [this.shop.imagePathBig.replace('big', '%%SIZE%%'), ...this.shop.additionalImagePaths]
      .concat([])
      .map((img, index) => {
        return {
          bigWebP: img.replace('%%SIZE%%', 'big').replace('jpg', 'webp'),
          // additionalImagePath small is super small 60x40, we need to use the 'big' one on swipes
          smallWebP: img.replace('%%SIZE%%', index === 0 ? 'small' : 'big').replace('jpg', 'webp'),
          big: img.replace('%%SIZE%%', 'big'),
          small: img.replace('%%SIZE%%', 'small')
        };
      });
  }

  ngOnInit() {
    this.crowdinService.watchLangChange({
      loaderMap: loaderMap,
      destroyRef: this.destroyRef,
      successCallback: () => {
        this.translateProperties();
        this.changeDetector.markForCheck();
      }
    });
    // this.addLCP();
    this.prepareShopImages();
    this.updateAdultsAndKids();
    this.prepareProductLink();
    this.prepareShopLink(false);
    this.updateShowGroupBookingTestCondition(false);
    this.selectedHotelService.onHotelChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.changeDetector.markForCheck();
    });
    this.platformInformationService.data$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: PlatformInformationInterface) => {
        this.cancellationInsuranceName = data.cancellation_insurance_name;
      });
    this.currencyService.onCurrencyChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(currency => {
      this.currencyCode = currency;
    });
    this.localizedRouterService.routerEvents
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lang: string) => {
        this.currentLang = lang;
        this.prepareProductLink();
        this.prepareShopLink(false);
        this.changeDetector.markForCheck();
      });
    this.resolutionService.sizeReferenceChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isSmallerThanLargeDevice = this.resolutionService.isSmallerThanLargeDevice();
      this.tickerRef?.setInput('triangle', !this.isSmallerThanLargeDevice);
      this.changeDetector.markForCheck();
      this.changeDetector.detectChanges();
    });
    this.countdownUntilBookingStop = this.minDelayCountdownService.getCountdownUntilBookingStop(
      this.shop,
      this.bookingParameters.start
    );
    this.minDelayCountdownService.paceMaker.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.countdownUntilBookingStop = this.minDelayCountdownService.getCountdownUntilBookingStop(
        this.shop,
        this.bookingParameters.start
      );
    });

    this.discountTickerService.discounts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(discounts => {
      const discount = discounts.find(
        d =>
          +d.timestamp > Date.now() / 1000 &&
          +this.shop.companyId === +d.companyId &&
          !d.excludeMerchantIds.split(',').includes(this.shop.merchantId.toString()) &&
          !d.excludeCountryCodes.split(',').includes(this.shop.countryCode)
      );
      if (discount) {
        this.initDiscountTicker(discount.timestamp);
      }
    });
    this.changeDetector.detectChanges();
  }

  protected async initDiscountTicker(timestamp: string) {
    const { DiscounttickerComponent } = await import(
      '../../../shared/saleslocation/shop/discountticker/discountticker.component'
    );
    this.discountTickerAnchor.clear();
    this.tickerRef = this.discountTickerAnchor.createComponent(DiscounttickerComponent);
    this.tickerRef.setInput('timestamp', timestamp);
    this.tickerRef.setInput('triangle', !this.isSmallerThanLargeDevice);
    (this.tickerRef.instance as DiscounttickerComponent).tickerEnded.subscribe(() => {
      this.discountTickerAnchor.clear();
      this.tickerRef = null;
    });
    this.changeDetector.detectChanges();
  }

  toggleMobileOffers() {
    this.openedMobileOffers = !this.openedMobileOffers;
    this.changeDetector.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propChange in changes) {
      if (Object.prototype.hasOwnProperty.call(changes, propChange)) {
        this[propChange] = changes[propChange].currentValue;
      }
    }
    // detached view, needs to check itself and its children manually
    this.changeDetector.detectChanges();
    if (changes['bookingParameters']) {
      setTimeout(() => {
        this.updateAdultsAndKids();
        this.prepareProductLink();
      });
    }
  }

  /**
   * Updates the position of the top/best-price-image, directly
   * under the shop-images-container
   * @param event
   */
  updateImagePosition(event) {
    if (!this.resolutionService.isSmallerThanLargeDevice()) {
      const imageWidth = 600;
      const boundingDiv = event.currentTarget.childNodes[1].closest('.shop-images').getBoundingClientRect();
      const x = event.clientX;
      const y = boundingDiv.bottom;
      // show image centered under the pointer
      let xCalc = x - 300;
      // prevent image going out of the viewport (right-side)
      if (x - 300 + imageWidth > window.innerWidth) {
        xCalc = window.innerWidth - imageWidth;
      }
      // prevent image going out of the viewport (left-side)
      if (xCalc < 0) {
        xCalc = boundingDiv.left;
      }
      event.currentTarget.childNodes[1].style.display = 'block';
      event.currentTarget.childNodes[1].style.top = y + 5 + 'px';
      event.currentTarget.childNodes[1].style.left = xCalc + 'px';
    }
  }

  onMouseLeave(event) {
    if (event.currentTarget.childNodes[1]?.style?.display) {
      event.currentTarget.childNodes[1].style.display = 'none';
    }
  }

  /**
   * displays map as overlay (overlay mode) or scrolls to the map.
   * highlights and centers shop on map
   * @param event
   */
  showOnMap(event) {
    event.preventDefault();
    this.shopDetailService.showOnMap.emit(this.shop);
  }

  validationAndSubmit($event: Event) {
    // TODO: we could let the shoplist do that on the goToProduct subscription.
    const unbookables = this.shop.unbookableReasonsFor(
      this.bookingParameters.start,
      this.bookingParameters.end,
      this.bookingParameters.ages
    );
    if (unbookables.length) {
      $event.preventDefault();
      let transkey: string;
      switch (unbookables[0].type) {
        case unbookableType.minDuration:
          transkey = 'NOTIFICATION.shopMinDuration';
          break;
        case unbookableType.maxDuration:
          transkey = 'NOTIFICATION.shopMaxDuration';
          break;
        case unbookableType.minDelay:
          transkey = 'NOTIFICATION.shopMinDelay';
          break;
        case unbookableType.after:
          transkey = 'NOTIFICATION.shopAfterSeason';
          break;
        case unbookableType.before:
          transkey = 'NOTIFICATION.shopBeforeSeason';
          break;
        case unbookableType.pause:
          transkey = 'NOTIFICATION.shopPause';
          break;
        case unbookableType.unservableAges:
          transkey = 'NOTIFICATION.shopUnservableAges';
          break;
      }
      this.translateService.get('CORE.notificationDateFormat').subscribe(dateFormat => {
        const notification: Notification = {
          displayTime: 0,
          transkey: transkey,
          transvars: {
            shop_name: this.shop.name,
            shop_address: this.shop.address,
            start_date: this.datePipe.transform(unbookables[0].start, dateFormat, undefined, 'en-US'),
            end_date: this.datePipe.transform(unbookables[0].end, dateFormat, undefined, 'en-US'),
            age: unbookables[0].age,
            min_delay: unbookables[0].minDelay,
            max_duration: unbookables[0].maxDuration,
            min_duration: unbookables[0].minDuration
          }
        };
        this.notificationService.add(notification);
      });
    }
  }

  private translateProperties() {
    this.translatedProperties = [];
    for (const prop of Object.keys(this.shop.rawProperties)) {
      // translate all vals;
      const vals = {};
      for (const propertyKey in this.shop.rawProperties[prop]) {
        if (Object.prototype.hasOwnProperty.call(this.shop.rawProperties[prop], propertyKey)) {
          // translate values of key
          if (Array.isArray(this.shop.rawProperties[prop][propertyKey])) {
            const values = [];
            // brands: [Bogner, Capranea, ...], etc
            for (const value of this.shop.rawProperties[prop][propertyKey]) {
              this.translateService.get('SHOP.property_' + value).subscribe((valTrans: string) => {
                if (valTrans.localeCompare('SHOP.property_' + value) !== 0) {
                  values.push(valTrans);
                } else {
                  values.push(value);
                }
              });
            }
            vals[propertyKey] = values.join(', ');
          } else {
            const transkey = 'SHOP.property_' + this.shop.rawProperties[prop][propertyKey];
            // size:1600, etc
            this.translateService.get(transkey).subscribe((valTrans: string) => {
              if (valTrans.localeCompare(transkey) !== 0) {
                vals[propertyKey] = valTrans;
              } else {
                vals[propertyKey] = this.shop.rawProperties[prop][propertyKey];
              }
            });
          }
        }
      }
      this.translateService.get('SHOP.property_' + prop, vals).subscribe((propTrans: string) => {
        this.translatedProperties.push(propTrans);
      });
    }
  }

  toggleOfferDetails() {
    this.showDetails = !this.showDetails;
    this.showDetailsEmitter.emit(this.showDetails);
  }

  toggleTooltip() {
    // i'm here to trigger an hover event on ipads.
    // see: https://stackoverflow.com/questions/18047353/fix-css-hover-on-iphone-ipad-ipod#20048559
  }

  openImageSlider(content: number) {
    if (this.resolutionService.isSmallerThanLargeDevice()) {
      this.shopImageIndex = content;
      this.changeDetector.detectChanges();
    }
  }

  rememberShop() {
    try {
      this.window.history.scrollRestoration = 'auto';
    } catch {
      /* empty */
    }
  }

  errorShopImage(index: number) {
    this.shopImages.splice(index, 1);
  }

  removeAdditionalImage(index: number) {
    this.shop.additionalImagePaths.splice(index, 1);
  }

  /**townSlugs
   * get the amount a single person has to pay per day on the package price
   */
  calculateDayPricePerPerson() {
    const people = this.bookingParameters.ages.length > 1 ? this.bookingParameters.ages.length : 1;

    return (
      this.shop.defaultProductOnlinePriceRaw /
      (Math.round(
        Math.abs(
          (this.bookingParameters.start.getTime() - this.bookingParameters.end.getTime()) /
            (24 * 60 * 60 * 1000)
        )
      ) +
        1) /
      people
    );
  }

  getDays() {
    return (
      Math.round(
        Math.abs(
          (this.bookingParameters.start.getTime() - this.bookingParameters.end.getTime()) /
            (24 * 60 * 60 * 1000)
        )
      ) + 1
    );
  }
}
